import { createContext, useContext, useEffect, useState } from 'react'
import { clearDemoSession, setDemoSession } from '../services/demoSession'

const AuthContext = createContext(null)

const RENTAL_TENANT_ROLE = 'RentalBizTenant'
const RENTAL_LANDLORD_ROLE = 'RentalBizLandlord'
const RENTAL_MODE_STORAGE_PREFIX = 'rentalbiz:active-mode:'

const getRoles = (userData) => userData?.Roles || userData?.roles || []

const getAssignedRentalBizMode = (roles) => {
  const hasTenant = roles.includes(RENTAL_TENANT_ROLE)
  const hasLandlord = roles.includes(RENTAL_LANDLORD_ROLE)

  if (hasTenant && hasLandlord) return 'both'
  if (hasLandlord) return 'landlord'
  if (hasTenant) return 'tenant'
  return null
}

const readPreferredMode = (userId) => {
  if (!userId) return null
  try {
    const value = localStorage.getItem(`${RENTAL_MODE_STORAGE_PREFIX}${userId}`)
    return value === 'tenant' || value === 'landlord' ? value : null
  } catch (error) {
    console.error('Error reading preferred rental mode:', error)
    return null
  }
}

const savePreferredMode = (userId, mode) => {
  if (!userId || (mode !== 'tenant' && mode !== 'landlord')) return
  try {
    localStorage.setItem(`${RENTAL_MODE_STORAGE_PREFIX}${userId}`, mode)
  } catch (error) {
    console.error('Error saving preferred rental mode:', error)
  }
}

const resolveActiveMode = (assignedMode, preferredMode) => {
  if (assignedMode === 'landlord' || assignedMode === 'tenant') return assignedMode
  if (assignedMode === 'both') return preferredMode === 'tenant' ? 'tenant' : 'landlord'
  return null
}

const parseJsonSafely = async (response) => {
  try {
    return await response.json()
  } catch {
    return {}
  }
}

const normalizeUser = (userData, forcedMode = null) => {
  const isDemo = Boolean(userData?.IsDemo || userData?.isDemo || userData?.is_demo)
  const roles = getRoles(userData)
  const assignedMode = getAssignedRentalBizMode(roles)
  const preferredMode = forcedMode || readPreferredMode(userData?.Id || userData?.id)
  const activeMode = resolveActiveMode(assignedMode, preferredMode)
  const isLandlord = activeMode === 'landlord'

  if (assignedMode === 'both' && activeMode) {
    savePreferredMode(userData?.Id || userData?.id, activeMode)
  }

  return {
    ...userData,
    id: userData?.Id || userData?.id,
    email: userData?.Email || userData?.email,
    first_name: userData?.FirstName || userData?.first_name,
    last_name: userData?.LastName || userData?.last_name,
    phone: userData?.PhoneNumber || userData?.phone_number || userData?.phone || '',
    Roles: roles,
    isDemo,
    demoProduct: userData?.DemoProduct || userData?.demoProduct || userData?.demo_product || null,
    demoProducts: userData?.DemoProducts || userData?.demoProducts || userData?.demo_products || [],
    rentalBizAssignedMode: assignedMode,
    rentalBizMode: activeMode,
    role: activeMode,
    isLandlord
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    checkAuth()
  }, [])

  const checkAuth = async () => {
    try {
      const response = await fetch('/api/auth/me', {
        credentials: 'include'
      })

      if (response.ok) {
        const userData = await parseJsonSafely(response)
        const normalized = normalizeUser(userData)
        setUser(normalized)
        setDemoSession(normalized)
      } else {
        setUser(null)
        clearDemoSession()
      }
    } catch (error) {
      console.error('Error checking auth:', error)
      setUser(null)
      clearDemoSession()
    } finally {
      setLoading(false)
    }
  }

  const login = async (email, password) => {
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ email, password })
    })

    if (!response.ok) {
      const error = await parseJsonSafely(response)
      throw new Error(error.message || 'Error al iniciar sesion')
    }

    const userData = await parseJsonSafely(response)
    const normalizedUser = normalizeUser(userData)
    setUser(normalizedUser)
    setDemoSession(normalizedUser)
    return normalizedUser
  }

  const register = async ({ firstName, lastName, email, password }) => {
    const registerResponse = await fetch('/api/auth/register', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({
        firstName,
        lastName,
        email,
        password
      })
    })

    const registerData = await parseJsonSafely(registerResponse)
    if (!registerResponse.ok) {
      const validationErrors = registerData?.errors
      if (validationErrors && typeof validationErrors === 'object') {
        const messages = Object.values(validationErrors).flat()
        throw new Error(messages[0] || registerData?.message || 'Error al registrar usuario')
      }
      throw new Error(registerData?.message || 'Error al registrar usuario')
    }

    if (registerData?.requiresEmailConfirmation) {
      return {
        requiresEmailConfirmation: true,
        message: registerData?.message || 'Revisa tu correo para verificar la cuenta.',
        email
      }
    }

    return await login(email, password)
  }

  const logout = async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include'
      })
    } catch (error) {
      console.error('Error during logout:', error)
    } finally {
      setUser(null)
      clearDemoSession()
    }
  }

  const setRentalBizRole = async (mode, activeMode = null) => {
    if (user?.isDemo) {
      const rolesToApply = mode === 'both'
        ? [RENTAL_LANDLORD_ROLE, RENTAL_TENANT_ROLE]
        : mode === 'tenant'
          ? [RENTAL_TENANT_ROLE]
          : [RENTAL_LANDLORD_ROLE]
      const mergedUser = {
        ...(user || {}),
        Roles: rolesToApply
      }
      const normalized = normalizeUser(mergedUser, activeMode)
      setUser(normalized)
      setDemoSession(normalized)
      return normalized
    }

    const response = await fetch('/api/auth/rentalbiz-role', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ mode })
    })

    const data = await parseJsonSafely(response)
    if (!response.ok) {
      throw new Error(data.message || 'No se pudo guardar el rol de RentalBiz')
    }

    const mergedUser = {
      ...(user || {}),
      Roles: data.Roles || []
    }

    const normalized = normalizeUser(mergedUser, activeMode)
    setUser(normalized)
    setDemoSession(normalized)
    return normalized
  }

  const setActiveRentalBizMode = (mode) => {
    if (!user || user.rentalBizAssignedMode !== 'both') return
    if (mode !== 'tenant' && mode !== 'landlord') return

    savePreferredMode(user.id, mode)
    setUser((prev) => ({
      ...prev,
      rentalBizMode: mode,
      role: mode,
      isLandlord: mode === 'landlord'
    }))
    setDemoSession({
      ...(user || {}),
      rentalBizMode: mode,
      role: mode,
      isLandlord: mode === 'landlord'
    })
  }

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    isLandlord: user?.isLandlord ?? false,
    rentalBizMode: user?.rentalBizMode ?? null,
    rentalBizAssignedMode: user?.rentalBizAssignedMode ?? null,
    login,
    register,
    logout,
    checkAuth,
    setRentalBizRole,
    setActiveRentalBizMode
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext
